import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useStore } from '../store/useStore';
import { deleteProject, getProjects } from '../utils/api';
import styles from './ProjectsPage.module.css';

export default function ProjectsPage() {
  const {
    projects, setProjects,
    setProject, setView,
    role, currentUser,
    setCreateProjectModalOpen,
  } = useStore();

  const [query, setQuery] = useState('');

  const canManage = role === 'admin' || role === 'pm';

  const filtered = projects.filter((p) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return [p.name, p.number, p.project_manager, p.superintendent, p.address]
      .filter(Boolean)
      .some((v) => v.toLowerCase().includes(q));
  });

  async function handleOpen(project) {
    setProject(project);
    setView('project');
  }

  async function handleDelete(e, project) {
    e.stopPropagation();
    if (!canManage) return;
    if (!window.confirm(`Delete "${project.name}"? This removes all sheets and markups.`)) return;
    try {
      await deleteProject(project.id);
      const fresh = await getProjects();
      setProjects(fresh);
      toast.success('Project deleted');
    } catch (err) {
      toast.error(err.message);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>PROJECTS</h1>
          <p className={styles.subtitle}>
            {currentUser
              ? <>Signed in as <span className={styles.you}>{currentUser.name}</span> · {projects.length} project{projects.length !== 1 ? 's' : ''}</>
              : `${projects.length} project${projects.length !== 1 ? 's' : ''}`}
          </p>
        </div>

        <div className={styles.actions}>
          <div className={styles.searchWrap}>
            <SearchIcon />
            <input
              className={styles.search}
              placeholder="Filter projects…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button className={styles.clearBtn} onClick={() => setQuery('')}>×</button>
            )}
          </div>
          {canManage && (
            <button
              className={styles.newBtn}
              onClick={() => setCreateProjectModalOpen(true)}
            >
              <PlusIcon />
              NEW PROJECT
            </button>
          )}
        </div>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Job Number</th>
              <th>Project Manager</th>
              <th>Superintendent</th>
              <th>Address</th>
              <th className={styles.thActions} />
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr
                key={p.id}
                className={styles.row}
                onClick={() => handleOpen(p)}
                tabIndex={0}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleOpen(p)}
              >
                <td>
                  <div className={styles.nameCell}>
                    <span className={styles.nameText}>{p.name}</span>
                    {p.description && <span className={styles.nameDesc}>{p.description}</span>}
                  </div>
                </td>
                <td data-label="Job #" className={styles.mono}>{p.number || '—'}</td>
                <td data-label="PM">{p.project_manager || <span className={styles.faint}>—</span>}</td>
                <td data-label="Super">{p.superintendent  || <span className={styles.faint}>—</span>}</td>
                <td data-label="Address" className={styles.addr}>{p.address || <span className={styles.faint}>—</span>}</td>
                <td className={styles.actionsCell}>
                  {canManage && (
                    <button
                      className={styles.delBtn}
                      onClick={(e) => handleDelete(e, p)}
                      title="Delete project"
                    >
                      <TrashIcon />
                    </button>
                  )}
                  <ChevronIcon className={styles.chev} />
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className={styles.empty}>
                  {projects.length === 0
                    ? (canManage
                        ? 'No projects yet. Create your first one →'
                        : 'You have not been invited to any projects yet.')
                    : 'No projects match your filter.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const SearchIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" className={styles.searchIcon}>
    <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
const PlusIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M3 4h10M6 4V2.5A.5.5 0 016.5 2h3a.5.5 0 01.5.5V4M4.5 4l.5 9a1 1 0 001 1h4a1 1 0 001-1l.5-9"
      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const ChevronIcon = ({ className }) => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className={className}>
    <path d="M4 2l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
